import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  Email,
  Phone,
  LocationOn,
  Lock,
  Shield,
  Verified,
} from '@mui/icons-material';
import { EMAILS, COMPANY } from '../../constants';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { name: 'Visa Check', path: '/visa-check' },
      { name: 'Countries', path: '/countries' },
      { name: 'Applications', path: '/applications' },
      { name: 'Pricing', path: '/pricing' },
    ],
    support: [
      { name: 'Help Center', path: '/help' },
      { name: 'Contact Us', path: '/contact' },
      { name: 'About Us', path: '/about' },
      { name: 'Settings', path: '/settings' },
    ],
    company: [
      { name: 'About Alpha Libra', path: '/about' },
      { name: 'Careers', path: '/careers' },
      { name: 'Press', path: '/press' },
      { name: 'Partners', path: '/partners' },
    ],
    legal: [
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Service', path: '/terms' },
      { name: 'Delete account', path: '/delete-account' },
      { name: 'Cookie Policy', path: '/cookies' },
      { name: 'Compliance', path: '/compliance' },
    ],
  };

  const socialLinks = [
    { name: 'Facebook', icon: <Facebook />, url: 'https://facebook.com/Tripnovac' },
    { name: 'Twitter', icon: <Twitter />, url: 'https://twitter.com/Tripnovac' },
    { name: 'LinkedIn', icon: <LinkedIn />, url: 'https://linkedin.com/company/Tripnovac' },
    { name: 'Instagram', icon: <Instagram />, url: 'https://instagram.com/Tripnovac' },
  ];

  const contactInfo = [
    { icon: <Email />, text: EMAILS.SUPPORT, href: `mailto:${EMAILS.SUPPORT}` },
    { icon: <Phone />, text: COMPANY.PHONE, href: 'tel:+18004640701' },
    { icon: <LocationOn />, text: `${COMPANY.LOCATION} — ${COMPANY.NAME}`, href: null },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#030B1B',
        color: 'white',
        pt: 6,
        pb: 3,
        mt: 'auto',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                component="img"
                src="/assets/Tripnovac-brand.png"
                alt="Tripnovac"
                sx={{ height: 36, width: 'auto' }}
                onError={(e: any) => { e.target.style.display = 'none'; }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(96.54deg, #1273E5 41.24%, #00BCEC 59.2%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Tripnovac Visa
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 3, color: 'grey.300' }}>
              Enterprise Visa Processing Platform by Alpha Libra LLC covering 249 countries and territories (ISO 3166-1)
              with AI-powered assistance, real-time tracking, and expert guidance.
            </Typography>
            
            {/* Contact Info */}
            <Box sx={{ mb: 3 }}>
              {contactInfo.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ mr: 1, color: 'primary.main' }}>{item.icon}</Box>
                  {item.href ? (
                    <Link
                      href={item.href}
                      color="grey.300"
                      sx={{ textDecoration: 'none', '&:hover': { color: 'primary.light' } }}
                    >
                      <Typography variant="body2">{item.text}</Typography>
                    </Link>
                  ) : (
                    <Typography variant="body2" color="grey.300">
                      {item.text}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            {/* Social Links */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {socialLinks.map((social) => (
                <IconButton
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'grey.400',
                    '&:hover': {
                      color: 'primary.light',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Services */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Services
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {footerLinks.services.map((link) => (
                <Link
                  key={link.name}
                  component={RouterLink}
                  to={link.path}
                  color="grey.300"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': { color: 'primary.light' },
                    transition: 'color 0.3s ease',
                  }}
                >
                  <Typography variant="body2">{link.name}</Typography>
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Support */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Support
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {footerLinks.support.map((link) => (
                <Link
                  key={link.name}
                  component={RouterLink}
                  to={link.path}
                  color="grey.300"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': { color: 'primary.light' },
                    transition: 'color 0.3s ease',
                  }}
                >
                  <Typography variant="body2">{link.name}</Typography>
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Company */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Company
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {footerLinks.company.map((link) => (
                <Link
                  key={link.name}
                  component={RouterLink}
                  to={link.path}
                  color="grey.300"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': { color: 'primary.light' },
                    transition: 'color 0.3s ease',
                  }}
                >
                  <Typography variant="body2">{link.name}</Typography>
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Legal */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Legal
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {footerLinks.legal.map((link) => (
                <Link
                  key={link.name}
                  component={RouterLink}
                  to={link.path}
                  color="grey.300"
                  sx={{
                    textDecoration: 'none',
                    '&:hover': { color: 'primary.light' },
                    transition: 'color 0.3s ease',
                  }}
                >
                  <Typography variant="body2">{link.name}</Typography>
                </Link>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.06)' }} />

        {/* Security Badges */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          {[
            { icon: <Lock sx={{ fontSize: 16 }} />, text: 'HTTPS Delivery' },
            { icon: <Shield sx={{ fontSize: 16 }} />, text: 'Role-Restricted Access' },
            { icon: <Verified sx={{ fontSize: 16 }} />, text: 'Stripe Card Handling' },
          ].map((badge, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ color: '#10B981' }}>{badge.icon}</Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>{badge.text}</Typography>
            </Box>
          ))}
        </Box>

        {/* Bottom section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="grey.400">
            © {currentYear} Tripnovac by Alpha Libra LLC. All rights reserved.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="grey.400">
              🌍 Global Coverage: 249 countries and territories (ISO 3166-1)
            </Typography>
            <Typography variant="body2" color="grey.400">
              ⚡ AI-Powered Processing
            </Typography>
            <Typography variant="body2" color="grey.400">
              🔒 Enterprise Security
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
